import mammoth from 'mammoth';

import type { MergeFieldIssue } from '../constants/merge-field-issue.constants';

import { ADD_TEMPLATE_STEP_THREE_MAX_FILE_BYTES } from '../validations/add-template.validation';

export type DocxMergeFieldAnalysis = {
  issues: MergeFieldIssue[];
  signatureAnchors: string[];
  variables: string[];
};

export class DocxAnalysisFileTooLargeError extends Error {
  constructor() {
    super('DOCX_TOO_LARGE');
    this.name = 'DocxAnalysisFileTooLargeError';
  }
}

export class DocxAnalysisInvalidDocxError extends Error {
  constructor() {
    super('DOCX_INVALID_OR_CORRUPT');
    this.name = 'DocxAnalysisInvalidDocxError';
  }
}

const MERGE_FIELD_NAME_ALLOWED = /^[A-Za-z0-9_]+$/;
const SIGNATURE_ANCHOR_REGEX = /(?<!\[)\[([^[\]\r\n]*)\](?!\])/g;

const isInvalidDocxArchiveError = (err: unknown): boolean => {
  if (!(err instanceof Error)) return false;
  const msg = err.message.toLowerCase();
  return (
    msg.includes('central directory') ||
    msg.includes('is this a zip file') ||
    msg.includes('invalid local file header') ||
    msg.includes('end of central directory record')
  );
};

export type AnalyzeDocxMergeFieldsOptions = {
  ignoreVariablePlaceholders?: boolean;
};

export const analyzeDocxMergeFields = async (
  file: File,
  options?: AnalyzeDocxMergeFieldsOptions,
): Promise<DocxMergeFieldAnalysis> => {
  const arrayBuffer = await file.arrayBuffer();
  if (arrayBuffer.byteLength > ADD_TEMPLATE_STEP_THREE_MAX_FILE_BYTES) {
    throw new DocxAnalysisFileTooLargeError();
  }

  let result: { value: string };
  try {
    result = await mammoth.extractRawText({ arrayBuffer });
  } catch (err) {
    if (isInvalidDocxArchiveError(err)) {
      throw new DocxAnalysisInvalidDocxError();
    }
    throw err;
  }

  const text = result.value;

  const seenValid = new Set<string>();
  const seenAnchors = new Set<string>();
  const issueKeys = new Set<string>();
  const issues: MergeFieldIssue[] = [];

  const ignoreVariablePlaceholders = options?.ignoreVariablePlaceholders ?? false;

  const re = /\{\{(.*?)\}\}/g;
  let match: null | RegExpExecArray;
  if (!ignoreVariablePlaceholders) {
    while ((match = re.exec(text)) !== null) {
      const inner = match[1].trim();

      if (inner === '') {
        const key = '__empty__';
        if (!issueKeys.has(key)) {
          issueKeys.add(key);
          issues.push({ code: 'EMPTY', field: '{{}}' });
        }
        continue;
      }

      if (inner.includes('{') || inner.includes('}')) {
        continue;
      }

      if (/\s/.test(inner)) {
        const key = inner;
        if (!issueKeys.has(key)) {
          issueKeys.add(key);
          issues.push({ code: 'CONTAINS_WHITESPACE', field: `{{${inner}}}` });
        }
        continue;
      }

      if (!MERGE_FIELD_NAME_ALLOWED.test(inner)) {
        const key = `invalid-symbols:${inner}`;
        if (!issueKeys.has(key)) {
          issueKeys.add(key);
          issues.push({ code: 'INVALID_SYMBOLS', field: `{{${inner}}}` });
        }
        continue;
      }

      if (!seenValid.has(inner)) {
        seenValid.add(inner);
      }
    }
  }

  while ((match = SIGNATURE_ANCHOR_REGEX.exec(text)) !== null) {
    const inner = match[1].trim();

    if (inner === '') {
      const key = '__anchor-empty__';
      if (!issueKeys.has(key)) {
        issueKeys.add(key);
        issues.push({ code: 'EMPTY', field: '[]' });
      }
      continue;
    }

    if (inner.includes('[') || inner.includes(']')) {
      continue;
    }

    if (/\s/.test(inner)) {
      const key = `anchor-whitespace:${inner}`;
      if (!issueKeys.has(key)) {
        issueKeys.add(key);
        issues.push({ code: 'CONTAINS_WHITESPACE', field: `[${inner}]` });
      }
      continue;
    }

    if (!MERGE_FIELD_NAME_ALLOWED.test(inner)) {
      const key = `anchor-invalid-symbols:${inner}`;
      if (!issueKeys.has(key)) {
        issueKeys.add(key);
        issues.push({ code: 'INVALID_SYMBOLS', field: `[${inner}]` });
      }
      continue;
    }

    seenAnchors.add(`[${inner}]`);
  }

  return {
    issues,
    signatureAnchors: Array.from(seenAnchors),
    variables: Array.from(seenValid),
  };
};
